import { useEffect, useMemo, useState } from 'react';
import { Compass, TriangleAlert } from 'lucide-react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { supabase } from '../../lib/supabase';
import styles from './attribution.module.css';

/**
 * "מאיפה בא הכסף" — revenue by acquisition source.
 *
 * Reads public.lead_attribution (migration 0016), which joins
 * campaign → lead → quote → project. Before that migration this page could not
 * exist: the source was Hebrew prose inside leads.notes and leads.source_id was
 * never written, so there was nothing to group by.
 *
 * Deliberately does NOT show ROAS, CPL or CAC. Ad spend does not live in this
 * system — it lives in Meta Ads Manager, Google Ads and a credit-card
 * statement. Dividing revenue by a number we do not have would produce a
 * confident-looking figure with no basis. The Cost column is there, empty, with
 * a note on what to connect. An honest gap beats a fabricated metric.
 */

const RANGES = [
  { days: 30, label: '30 יום' },
  { days: 90, label: '90 יום' },
  { days: 365, label: 'שנה' },
] as const;

interface AttributionRow {
  lead_id: string | null;
  received_at: string | null;
  status: string | null;
  score: number | null;
  source: string | null;
  source_label: string | null;
  medium: string | null;
  campaign: string | null;
  entry_point: string | null;
  quoted_amount: number | null;
  accepted_amount: number | null;
}

interface Bucket {
  key: string;
  label: string;
  leads: number;
  converted: number;
  quoted: number;
  accepted: number;
  scoreSum: number;
  scoreCount: number;
}

const ENTRY_POINT_LABELS: Record<string, string> = {
  'form-website': 'טופס באתר',
  'whatsapp-bot': 'בוט WhatsApp',
  'direct-whatsapp': 'WhatsApp ישיר',
  ctwa: 'מודעה ל-WhatsApp',
};

const MEDIUM_LABELS: Record<string, string> = {
  cpc: 'ממומן',
  organic: 'אורגני',
  social: 'סושיאל',
  referral: 'הפניה',
};

function formatILS(amount: number): string {
  return `₪ ${new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(amount)}`;
}

function pct(part: number, whole: number): string {
  if (whole === 0) return '—';
  return `${String(Math.round((part / whole) * 100))}%`;
}

/** Group rows into buckets by an arbitrary key extractor. */
function bucketBy(
  rows: AttributionRow[],
  keyOf: (r: AttributionRow) => string | null,
  labelOf: (r: AttributionRow, key: string) => string,
): Bucket[] {
  const map = new Map<string, Bucket>();
  for (const r of rows) {
    const key = keyOf(r);
    if (key === null) continue;
    let b = map.get(key);
    if (!b) {
      b = {
        key,
        label: labelOf(r, key),
        leads: 0,
        converted: 0,
        quoted: 0,
        accepted: 0,
        scoreSum: 0,
        scoreCount: 0,
      };
      map.set(key, b);
    }
    b.leads += 1;
    if (r.status === 'converted') b.converted += 1;
    b.quoted += r.quoted_amount ?? 0;
    b.accepted += r.accepted_amount ?? 0;
    if (r.score != null) {
      b.scoreSum += r.score;
      b.scoreCount += 1;
    }
  }
  // Revenue first — this page exists to answer "which source pays".
  return [...map.values()].sort((a, b) => b.accepted - a.accepted || b.leads - a.leads);
}

function BucketTable({
  rows,
  emptyText,
  firstColLabel,
}: {
  rows: Bucket[];
  emptyText: string;
  firstColLabel: string;
}) {
  if (rows.length === 0) return <p className={styles.notice}>{emptyText}</p>;
  const maxAccepted = Math.max(...rows.map((r) => r.accepted), 1);

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>{firstColLabel}</th>
          <th className={styles.numCol}>לידים</th>
          <th className={styles.numCol}>הומרו</th>
          <th className={styles.numCol}>יחס המרה</th>
          <th className={styles.numCol}>ניקוד ממוצע</th>
          <th className={styles.numCol}>בהצעות</th>
          <th className={styles.numCol}>הכנסה מאושרת</th>
          <th className={styles.numCol}>עלות</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td>
              <span className={styles.srcName}>{r.label}</span>
              <span
                className={styles.srcBar}
                style={{ width: `${String((r.accepted / maxAccepted) * 100)}%` }}
                aria-hidden="true"
              />
            </td>
            <td className={styles.numCol}>{String(r.leads)}</td>
            <td className={styles.numCol}>{String(r.converted)}</td>
            <td className={styles.numCol}>{pct(r.converted, r.leads)}</td>
            <td className={styles.numCol}>
              {r.scoreCount > 0 ? String(Math.round(r.scoreSum / r.scoreCount)) : '—'}
            </td>
            <td className={styles.numCol}>{formatILS(r.quoted)}</td>
            <td className={styles.numColStrong}>{formatILS(r.accepted)}</td>
            <td className={styles.numColMuted} title="עלות מדיה לא מחוברת למערכת">
              —
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AttributionPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const [days, setDays] = useState<number>(90);
  const [rows, setRows] = useState<AttributionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    const cancel = { current: false };
    setLoading(true);
    setError(null);

    void (async () => {
      const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      const { data, error: err } = await supabase
        .from('lead_attribution')
        .select(
          'lead_id, received_at, status, score, source, source_label, medium, campaign, entry_point, quoted_amount, accepted_amount',
        )
        .eq('organization_id', orgId)
        .gte('received_at', since)
        .order('received_at', { ascending: false })
        .limit(5000);

      if (cancel.current) return;
      if (err) {
        // The most likely cause is that migration 0016 has not been pushed yet.
        setError(err.message);
        setRows([]);
      } else {
        setRows(data);
      }
      setLoading(false);
    })();

    return () => {
      cancel.current = true;
    };
  }, [orgId, days]);

  const bySource = useMemo(
    () =>
      bucketBy(
        rows,
        (r) => r.source ?? 'direct',
        (r, key) => {
          const label = r.source_label ?? (key === 'direct' ? 'ישיר / לא מזוהה' : key);
          const medium = r.medium ? MEDIUM_LABELS[r.medium] ?? r.medium : null;
          return medium ? `${label} · ${medium}` : label;
        },
      ),
    [rows],
  );

  const byCampaign = useMemo(
    () => bucketBy(rows, (r) => r.campaign, (_r, key) => key),
    [rows],
  );

  const byEntryPoint = useMemo(
    () =>
      bucketBy(
        rows,
        (r) => r.entry_point,
        (_r, key) => ENTRY_POINT_LABELS[key] ?? key,
      ),
    [rows],
  );

  const totals = useMemo(() => {
    const leads = rows.length;
    // "Attributed" means we can name a source other than the fallback. This is
    // the health metric for the tracking itself, not a business metric.
    const attributed = rows.filter((r) => r.source && r.source !== 'direct').length;
    const accepted = rows.reduce((s, r) => s + (r.accepted_amount ?? 0), 0);
    const quoted = rows.reduce((s, r) => s + (r.quoted_amount ?? 0), 0);
    const converted = rows.filter((r) => r.status === 'converted').length;
    return { leads, attributed, accepted, quoted, converted };
  }, [rows]);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1>מאיפה בא הכסף</h1>
          <p className={styles.sub}>
            ייחוס הכנסה למקור הרכישה — קמפיין → ליד → הצעה → פרויקט.
          </p>
        </div>
        <Compass size={32} className={styles.headIcon} aria-hidden="true" />
      </header>

      <div className={styles.rangeBar} role="group" aria-label="טווח תאריכים">
        {RANGES.map((r) => (
          <button
            key={r.days}
            type="button"
            className={r.days === days ? styles.rangeOn : styles.rangeOff}
            onClick={() => { setDays(r.days); }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className={styles.errorCard}>
          <TriangleAlert size={20} aria-hidden="true" />
          <div>
            <strong>לא ניתן לקרוא את נתוני הייחוס.</strong>
            <p>
              הסיבה הסבירה: מיגרציה <code dir="ltr">0016_lead_attribution</code> עוד לא הורצה.
              הרץ <code dir="ltr">pnpm db:push</code> ואחר כך <code dir="ltr">pnpm db:types</code>.
            </p>
            <p className={styles.errorDetail} dir="ltr">{error}</p>
          </div>
        </div>
      ) : loading ? (
        <p className={styles.notice}>טוען…</p>
      ) : (
        <>
          <div className={styles.kpiGrid}>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>לידים בטווח</span>
              <span className={styles.kpiValue}>{String(totals.leads)}</span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>מיוחסים למקור</span>
              <span className={styles.kpiValue}>{pct(totals.attributed, totals.leads)}</span>
              <span className={styles.kpiHint}>
                {String(totals.attributed)} מתוך {String(totals.leads)}
              </span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>הכנסה מאושרת</span>
              <span className={styles.kpiValue}>{formatILS(totals.accepted)}</span>
              <span className={styles.kpiHint}>הצעות בסטטוס «אושרה»</span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>בצינור</span>
              <span className={styles.kpiValue}>{formatILS(totals.quoted)}</span>
              <span className={styles.kpiHint}>סך ההצעות שנשלחו</span>
            </div>
          </div>

          {totals.leads > 0 && totals.attributed === 0 && (
            <div className={styles.warnCard}>
              <TriangleAlert size={18} aria-hidden="true" />
              <span>
                אף ליד בטווח הזה לא נושא מקור. אם המיגרציה כבר רצה, בדוק שהאתר
                מעביר <code dir="ltr">attribution</code> — כלומר ש-
                <code dir="ltr">SAAS_LEAD_INTAKE_URL</code> ו-
                <code dir="ltr">SAAS_LEAD_INTAKE_SECRET</code> מוגדרים ב-Vercel של האתר.
              </span>
            </div>
          )}

          <div className={styles.section}>
            <h3>לפי מקור</h3>
            <BucketTable
              rows={bySource}
              firstColLabel="מקור"
              emptyText="אין לידים בטווח הזה."
            />
          </div>

          <div className={styles.section}>
            <h3>לפי קמפיין</h3>
            <BucketTable
              rows={byCampaign}
              firstColLabel="קמפיין"
              emptyText="אין לידים עם קמפיין מזוהה. קמפיין נקלט מ-utm_campaign בקישור, או ממזהה המודעה בשיחות שנפתחו ממודעת WhatsApp."
            />
          </div>

          <div className={styles.section}>
            <h3>לפי נקודת כניסה</h3>
            <BucketTable
              rows={byEntryPoint}
              firstColLabel="נקודת כניסה"
              emptyText="אין נתוני נקודת כניסה בטווח הזה."
            />
          </div>

          <p className={styles.notice}>
            ⓘ עמודת «עלות» ריקה בכוונה. עלות המדיה יושבת ב-Meta Ads Manager וב-Google Ads
            ולא במערכת, ולכן אין כאן ROAS או עלות לליד — חלוקה במספר שאין לנו הייתה מייצרת
            מדד שנראה אמין ואינו. כדי לסגור את זה צריך לייבא עלות יומית לפי קמפיין; ראה{' '}
            <code dir="ltr">docs/audit-2026-08/attribution.md</code>.
          </p>
        </>
      )}
    </div>
  );
}
